import { LeagueGameEvent } from '@insights-gaming/game-events';
import { createSelector } from '@reduxjs/toolkit';
import { Riot } from '../types';
import { FetchStatus } from '@insights-gaming/redux-utils';
import { LeagueStats, StatisticsState } from '../slice';

type Dictionary<T> = Record<string, T>;

type StatsSelector<R extends {}, T> = (r: R) => T;
type SelectorFactory<R extends {}, T, P> = () => (r: R, p: P) => T;

export interface StatisticsSelectors<R extends {} = any> {
  [LeagueGameEvent.CLASS_ID]: {
    getLeagueStats: StatsSelector<R, LeagueStats>;
    getLeagueStatsDict: StatsSelector<R, Partial<Dictionary<Riot.League.RawDataInfo>>>;
    getLeagueStatsAssetsDict: StatsSelector<R, Partial<Dictionary<Riot.League.Assets>>>;
    getLeagueStatsFetchStatusDict: StatsSelector<R, Partial<Dictionary<FetchStatus>>>;
    getLeagueStatsAssetsFetchStatusDict: StatsSelector<R, Partial<Dictionary<FetchStatus>>>;
    makeGetLeagueStatsByVideoId: SelectorFactory<R, Riot.League.RawDataInfo | undefined, { videoId: string }>;
    makeGetFetchStatusByVideoId: SelectorFactory<R, FetchStatus | undefined, { videoId: string }>;
    makeGetLeagueAssetsByGameVersion: SelectorFactory<R, Riot.League.Assets | undefined, { gameVersion: string }>;
    makeGetAssetsFetchStatusByGameVersion: SelectorFactory<R, FetchStatus | undefined, { gameVersion: string }>;
  };
}

export function makeStatisticsSelectors<R extends {} = any>(getStats: (rootReducer: R) => StatisticsState): StatisticsSelectors<R> {
  const getVideoIdFromProps = (_: R, props: { videoId: string }) => props.videoId;
  const getGameVersionFromProps = (_: R, props: { gameVersion: string }) => props.gameVersion;

  const getLeagueStats = createSelector(
    [getStats],
    (stats) => stats[LeagueGameEvent.CLASS_ID],
  );

  const getLeagueStatsDict = createSelector(
    [getLeagueStats],
    (stats) => stats.leagueStats,
  );

  const getLeagueStatsFetchStatusDict = createSelector(
    [getLeagueStats],
    (stats) => stats.fetchStatus,
  );

  const getLeagueStatsAssetsDict = createSelector(
    [getLeagueStats],
    (stats) => stats.leagueAssets,
  );

  const getLeagueStatsAssetsFetchStatusDict = createSelector(
    [getLeagueStats],
    (stats) => stats.assetsFetchStatus,
  );

  const makeGetLeagueStatsByVideoId = () => createSelector(
    [getLeagueStatsDict, getVideoIdFromProps],
    (dict, id) => {
      return dict[id];
    },
  );

  const makeGetFetchStatusByVideoId = () => createSelector(
    [getLeagueStatsFetchStatusDict, getVideoIdFromProps],
    (dict, id) => {
      return dict[id];
    },
  );

  const makeGetLeagueAssetsByGameVersion = () => createSelector(
    [getLeagueStatsAssetsDict, getGameVersionFromProps],
    (dict, id) => {
      return dict[id];
    },
  );

  const makeGetAssetsFetchStatusByGameVersion = () => createSelector(
    [getLeagueStatsAssetsFetchStatusDict, getGameVersionFromProps],
    (dict, id) => {
      return dict[id];
    },
  );

  return {
    [LeagueGameEvent.CLASS_ID]: {
      getLeagueStats,
      getLeagueStatsDict,
      getLeagueStatsAssetsDict,
      getLeagueStatsFetchStatusDict,
      getLeagueStatsAssetsFetchStatusDict,
      makeGetLeagueStatsByVideoId,
      makeGetFetchStatusByVideoId,
      makeGetLeagueAssetsByGameVersion,
      makeGetAssetsFetchStatusByGameVersion,
    },
  };
}
